import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import './OnboardingModal.css';

// Expanded view: 11 stages - smooth blue gradient (light to dark)
const STAGES = [
  { id: 'form_submitted', name: 'Form In', color: '#dbeafe' },           // blue-100
  { id: 'contract_sent', name: 'Onboard Form Out', color: '#bfdbfe' }, // blue-200
  { id: 'contract_signed', name: 'Onboard Form Done', color: '#93c5fd' },  // blue-300
  { id: 'cognito_created', name: 'Cognito', color: '#60a5fa' },          // blue-400
  { id: 'tiktok_outreach', name: 'Download TikTok', color: '#3b82f6' },  // blue-500
  { id: 'snapchat_tips', name: 'Snap Guide', color: '#2563eb' },         // blue-600
  { id: 'verified', name: 'Verified', color: '#1d4ed8' },                // blue-700
  { id: 'admin_access', name: 'Access', color: '#1e40af' },              // blue-800
  { id: 'monetized', name: '$$$', color: '#1e3a8a' },                    // blue-900
  { id: 'hyperwallet_setup', name: 'Hyperwallet Guide', color: '#172554' }, // blue-950
  { id: 'hyperwallet_confirmed', name: 'Hyperwallet Confirmed', color: '#0f172a' } // slate-900 (darkest)
];

// SVG Icons
const ICONS = {
  sent: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
    </svg>
  ),
  signed: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 19l7-7 3 3-7 7-3-3z M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z M2 2l7.586 7.586"/>
    </svg>
  ),
  download: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
    </svg>
  ),
  check: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M13 4L6 11L3 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
};

const OnboardingModal = ({ isOpen, onClose, clientId, clientName }) => {
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && clientId) {
      fetchOnboardingStatus();
    }
  }, [isOpen, clientId]);

  const fetchOnboardingStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.ONBOARDING_STATUS(clientId), {
        params: { expanded: true }
      });
      setStatusData(response.data);
    } catch (err) {
      console.error('Error fetching onboarding status:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    try {
      const date = new Date(timestamp);
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yy = String(date.getFullYear()).slice(-2);
      let hours = date.getHours();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 becomes 12
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return { date: `${mm}/${dd}/${yy}`, time: `${hours}:${minutes} ${ampm} PT` };
    } catch (e) {
      return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="onboarding-modal-overlay-onboardtrack" onClick={onClose}>
      <div className="onboarding-modal-content-onboardtrack" onClick={(e) => e.stopPropagation()}>
        <div className="onboarding-modal-header-onboardtrack">
          <h2>{clientName}</h2>
          <button className="onboarding-modal-close-onboardtrack" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="onboarding-modal-loading-onboardtrack">Loading...</div>
        ) : statusData ? (
          <div className="onboarding-modal-body-onboardtrack">
            {/* Summary Stats */}
            <div className="onboarding-stats-onboardtrack">
              <div className="stat-item-onboardtrack">
                <div className="stat-label-onboardtrack">Current Stage</div>
                <div className="stat-value-onboardtrack">{statusData.current_stage_name}</div>
              </div>
              <div className="stat-item-onboardtrack">
                <div className="stat-label-onboardtrack">Completion</div>
                <div className="stat-value-onboardtrack">{statusData.completion_percentage}%</div>
              </div>
              <div className="stat-item-onboardtrack">
                <div className="stat-label-onboardtrack">Pending Tasks</div>
                <div className="stat-value-onboardtrack">{statusData.pending_automations?.length || 0}</div>
              </div>
            </div>

            {/* Horizontal Progress Bar */}
            <div className="onboarding-horizontal-progress-onboardtrack">
              {STAGES.map((stage, index) => {
                const stageData = statusData.stages?.[index];
                const isCompleted = stageData?.completed;
                const isCurrent = index === statusData.current_stage;
                
                return (
                  <div key={stage.id} className="horizontal-stage-item-onboardtrack">
                    <div 
                      className={`horizontal-stage-circle-onboardtrack ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
                      style={{
                        backgroundColor: isCompleted ? stage.color : '#f3f4f6',
                        borderColor: isCurrent ? stage.color : '#e5e7eb'
                      }}
                    >
                      {isCompleted ? (
                        ICONS.check
                      ) : stage.iconType ? (
                        <span style={{ color: '#9ca3af' }}>{ICONS[stage.iconType]}</span>
                      ) : null}
                    </div>
                    <div className="horizontal-stage-label-onboardtrack">{stage.name}</div>
                    {stageData?.timestamp && formatTimestamp(stageData.timestamp) && (
                      <div className="horizontal-stage-time-onboardtrack">
                        <div>{formatTimestamp(stageData.timestamp).date}</div>
                        <div style={{ fontSize: '10px', color: '#9ca3af' }}>{formatTimestamp(stageData.timestamp).time}</div>
                      </div>
                    )}
                    {index < STAGES.length - 1 && (
                      <div 
                        className="horizontal-stage-connector-onboardtrack"
                        style={{
                          backgroundColor: isCompleted ? stage.color : '#e5e7eb'
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="onboarding-modal-actions-onboardtrack">
              <button className="btn-refresh-onboardtrack" onClick={fetchOnboardingStatus}>
                Refresh Status
              </button>
              <button className="btn-close-modal-onboardtrack" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="onboarding-modal-error-onboardtrack">Failed to load data</div>
        )}
      </div>
    </div>
  );
};

export default OnboardingModal;
